<template>
  <div
    class="draw-card vertical"
    tabindex="0"
    role="button"
    @click="handleCardClick"
  >
    <div
      class="card-top"
      @click="navigateToDetail"
    >
      <h3
        class="lottery-name"
        :title="lottery.game_name"
      >
        {{ lottery.game_name }}
      </h3>
      <button
        v-if="!isLite"
        class="favorite-btn"
        :class="{ active: isFavorited }"
        @click.stop="$emit('favorite')"
      >
        <div class="favorite-icon-wrapper">
          <svg 
            viewBox="0 0 24 24" 
            class="favorite-icon-svg"
            :class="{ filled: isFavorited }"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
      </button>
    </div>
    
    <div
      class="card-middle"
      @click="navigateToDetail"
    >
      <div class="flag-container">
        <CountryAvatar
          v-if="isLite"
          :country="lottery.country"
          :fallback-label="lottery.game_name"
          size="md"
        />
        <div
          v-else
          class="flag-border"
        >
          <img
            :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)"
            :alt="lottery.country"
            class="flag"
          >
        </div>
      </div>

      <div class="draw-info">
        <div class="countdown">
          <div class="time-block">
            <div class="time">
              {{ String(countdown.hours).padStart(2, '0') }}
            </div>
            <div class="label">
              {{ $t('time.hours') }}
            </div>
          </div>
          <div class="time-block">
            <div class="time">
              {{ String(countdown.minutes).padStart(2, '0') }}
            </div>
            <div class="label">
              {{ $t('time.mins') }}
            </div>
          </div>
          <div class="time-block">
            <div class="time">
              {{ String(countdown.seconds).padStart(2, '0') }}
            </div>
            <div class="label">
              {{ $t('time.secs') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <button
      v-if="!betClosed"
      class="bet-button"
      @click.stop="$emit('bet')"
    >
      {{ $t('betslip.betNow') }}  
    </button>
    <button
      v-else
      class="bet-button"
      disabled
    >
      {{ $t('betslip.betClosed') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { getGameLogoUrl } from '@/utils/resultParser';
import type { UpcomingDraw } from '@/types/lottery';
import { defineProps, defineEmits } from 'vue';
import { useIntegrationMode } from '@/composables/useIntegrationMode';
import CountryAvatar from '@/components/base/CountryAvatar.vue';

defineProps<{
  lottery: UpcomingDraw;
  isFavorited: boolean;
  countdown: { hours: number; minutes: number; seconds: number };
  favoriteIcon: string;
  favoriteFilledIcon: string;
  betClosed: boolean;
}>();

const emit = defineEmits(['favorite', 'bet', 'navigate']);
const { isLite } = useIntegrationMode();

const navigateToDetail = () => {
  emit('navigate');
};

const handleCardClick = (event: MouseEvent) => {
  if (event.target === event.currentTarget) {
    navigateToDetail();
  }
};
</script>

<style scoped>
.draw-card.vertical {
  background: linear-gradient(
    to bottom,
    var(--vertical-lottery-card-top-bg, var(--surface-card-face, var(--colour-surface))) 0,
    var(--vertical-lottery-card-top-bg, var(--surface-card-face, var(--colour-surface))) var(--vertical-lottery-card-split, 45%),
    var(--vertical-lottery-card-lower-bg, var(--surface-card-band, var(--colour-surface-cont-low))) var(--vertical-lottery-card-split, 45%),
    var(--vertical-lottery-card-lower-bg, var(--surface-card-band, var(--colour-surface-cont-low))) 100%
  );
  display: flex;
  flex-direction: column;
  width: var(--vertical-lottery-card-width, 210.78px);
  min-width: var(--vertical-lottery-card-width, 210.78px);
  max-width: var(--vertical-lottery-card-width, 210.78px);
  height: var(--vertical-lottery-card-height, 320.26px);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: var(--vertical-lottery-card-border-width, 1px) solid var(--vertical-lottery-card-border-color, var(--colour-surface-cont));
  border-radius: var(--vertical-lottery-card-radius, var(--radius-md));
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  padding: 0;
  box-sizing: border-box;
}

.draw-card.vertical:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px var(--shadow-color);
}

.card-top {
  padding: var(--vertical-lottery-card-top-padding, 14px 16px 10px);
  z-index: 2;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--vertical-lottery-card-top-gap, 4px);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.lottery-name {
  color: var(--lottery-name-color, var(--text-card-title));
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--vertical-lottery-card-title-size, 15px);
  font-weight: var(--vertical-lottery-card-title-weight, 700);
  letter-spacing: var(--vertical-lottery-card-title-spacing, 0.01em);
  margin: 0;
  line-height: 1.2;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: var(--vertical-lottery-card-title-white-space, normal);
  display: -webkit-box;
  -webkit-line-clamp: var(--vertical-lottery-card-title-lines, 2);
  -webkit-box-orient: vertical;
  min-height: var(--vertical-lottery-card-title-min-height, 2.4em);
}

.favorite-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.favorite-icon-wrapper {
  width: var(--vertical-lottery-card-favorite-size, 24px);
  height: var(--vertical-lottery-card-favorite-size, 24px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--vertical-lottery-card-favorite-radius, var(--favorite-icon-bg-radius, 50%));
}

.favorite-icon-svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: var(--vertical-lottery-card-favorite-color, var(--favorite-icon-color, var(--text-card-title)));
  stroke-width: var(--vertical-lottery-card-favorite-stroke, 2px);
  transition: all 0.2s ease;
}

.favorite-icon-svg.filled {
  fill: var(--vertical-lottery-card-favorite-active-color, var(--favorite-icon-active-color, var(--colour-orange-button)));
  stroke: var(--vertical-lottery-card-favorite-active-color, var(--favorite-icon-active-color, var(--colour-orange-button)));
}

.card-middle {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: var(--vertical-lottery-card-flag-countdown-gap, 12px);
  padding: var(--vertical-lottery-card-middle-top-padding, 0) 16px var(--vertical-lottery-card-middle-bottom-padding, 52px);
  box-sizing: border-box;
  min-height: 0;
  z-index: 2;
}

.flag-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}

.flag-border {
  width: var(--vertical-lottery-card-flag-size, 80.17px);
  height: var(--vertical-lottery-card-flag-size, 80.17px);
  background: var(--vertical-lottery-card-flag-border-color, var(--colour-surface-cont-low, #1D2636));
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--vertical-lottery-card-flag-padding, 8.77px);
  box-sizing: border-box;
}

.flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.draw-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0;
  padding-bottom: 0;
  z-index: 2;
}

.countdown {
  display: flex;
  justify-content: center;
  gap: var(--vertical-lottery-card-countdown-gap, 12px);
  background: var(--vertical-lottery-card-countdown-bg, transparent);
  padding: var(--vertical-lottery-card-countdown-padding, 0);
  margin: var(--vertical-lottery-card-countdown-margin, 0 auto);
  width: var(--vertical-lottery-card-countdown-width, auto);
  max-width: var(--vertical-lottery-card-countdown-max-width, 100%);
  box-sizing: border-box;
  border-radius: var(--vertical-lottery-card-countdown-radius, 0);
}

.time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time {
  font-size: var(--vertical-lottery-card-time-size, 18.58px);
  font-weight: var(--vertical-lottery-card-time-weight, 700);
  font-style: regular;
  color: var(--vertical-lottery-card-time-color, var(--text-on-card-contrast));
}

.label {
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--vertical-lottery-card-label-size, var(--horizontal-lottery-card-label-size, 11px));
  font-weight: 400;
  line-height: 1;
  text-align: center;
  color: var(--vertical-lottery-card-label-color, var(--text-on-card-contrast));
  opacity: var(--vertical-lottery-card-label-opacity, var(--horizontal-lottery-card-label-opacity, 0.9));
  text-transform: none;
  margin-top: var(--vertical-lottery-card-label-margin-top, var(--horizontal-lottery-card-label-margin-top, 4px));
}

.bet-button {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: var(--vertical-lottery-card-button-height, 46.92px);
  background: var(--vertical-lottery-card-button-bg, var(--colour-orange-button));
  border: none;
  color: var(--vertical-lottery-card-button-color, var(--text-on-card-contrast));
  font-size: var(--vertical-lottery-card-button-size, 13.78px);
  font-weight: var(--vertical-lottery-card-button-weight, 700);
  font-style: regular;
  cursor: pointer;
  text-transform: none;
  z-index: 2;
}

/* Mobile-only review adjustments (Aug 7) */
@media (max-width: 768px) {
  .draw-card.vertical {
    width: var(--vertical-lottery-card-mobile-width, 160px);
    min-width: var(--vertical-lottery-card-mobile-width, 160px);
    max-width: var(--vertical-lottery-card-mobile-width, 160px);
    height: var(--vertical-lottery-card-mobile-height, 280px);
    transition: none;
  }

  .draw-card.vertical:hover {
    transform: none;
    box-shadow: none;
  }

  .card-top {
    padding: var(--vertical-lottery-card-mobile-top-padding, 10px 12px 4px);
    gap: var(--vertical-lottery-card-mobile-top-gap, 2px);
  }

  .card-middle {
    gap: var(--vertical-lottery-card-mobile-flag-countdown-gap, 4px);
    padding-bottom: var(--vertical-lottery-card-mobile-middle-bottom-padding, 46px);
  }
  
  .flag-border {
    width: var(--vertical-lottery-card-mobile-flag-size, 60px);
    height: var(--vertical-lottery-card-mobile-flag-size, 60px);
    padding: var(--vertical-lottery-card-mobile-flag-padding, 6px);
  }
  
  .lottery-name {
    font-size: var(--vertical-lottery-card-mobile-title-size, 13px);
    font-weight: var(--vertical-lottery-card-mobile-title-weight, 500);
    text-overflow: unset;
    line-height: 1.25;
    min-height: var(--vertical-lottery-card-mobile-title-min-height, 2.5em);
  }

  .favorite-icon-wrapper {
    width: var(--vertical-lottery-card-mobile-favorite-size, 16px);
    height: var(--vertical-lottery-card-mobile-favorite-size, 16px);
  }

  .favorite-icon-svg {
    stroke-width: var(--vertical-lottery-card-mobile-favorite-stroke, 1.75px);
  }
  
  .time {
    font-size: var(--vertical-lottery-card-mobile-time-size, 18px);
  }

  .label {
    font-size: var(--vertical-lottery-card-mobile-label-size, var(--horizontal-lottery-card-mobile-label-size, 9px));
    margin-top: var(--vertical-lottery-card-mobile-label-margin-top, 1px);
  }
  
  .bet-button {
    height: var(--vertical-lottery-card-mobile-button-height, 40px);
    font-size: var(--vertical-lottery-card-mobile-button-size, 12px);
    font-weight: var(--vertical-lottery-card-mobile-button-weight, 600);
  }
}
</style>
